import { useEffect, useRef, useState } from "react";
import { Columns3Icon, DownloadIcon } from "lucide-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useDebouncedCallback } from "use-debounce";
import styled from "styled-components";
import {
  Button,
  DropDownMenu,
  TextInput,
} from "@monolith-forensics/monolith-ui";
import synchronizeColumnState from "../../../../utils/synchronize-column-state";
import UserRoleDefs from "../../../../components/ColumnDefinitions/UserRoleDefs";
import UserRolesAPI from "../../../../api/UserRoles/UserRoles";
import { getDateFormat } from "../../../../utils/date-format";
import { useNavigate, useParams } from "react-router-dom";
import { usePermissions } from "../../../../hooks/usePermissions";
import Table, { Column } from "../../../../Monolith-UI/Table/Table";
import { CreateUserRoleModal } from "@/components/Modals";

import AccessDeniedMessage from "../../../../components/AccessDeniedMessage";
import { ColumnProps } from "@/Monolith-UI/Table/types/Table";
import { UserRole } from "../../types/Users";
import UserRoleFlyout from "@/components/Flyouts/UserRoleFlyout";

interface UserRolesProps {
  className?: string;
}

interface TableMenuProps {
  className?: string;
  onAddItem?: () => void;
  columnState?: any;
  handleColumnVisibility?: (columns: any) => void;
  onExportTable?: (option: string) => void;
  onSearch?: (e: any) => void;
}

interface UserRolesTableProps {
  className?: string;
  onAddItem?: () => void;
  onRowAction?: (rowData: any) => void;
  tableRef?: any;
  selectedRow?: any;
}

interface Query {
  pageSize: number;
  search?: string;
  sort?: string;
  filter?: string;
  page?: number;
  dir?: string;
}

const pageSize = 50;
const queryKeyPrefix = ["trace-attributes", "list"];
const stateStoreKey = "case-analysis:trace-attributes";

const TableMenu = styled(
  ({
    className,
    onAddItem,
    columnState,
    handleColumnVisibility,
    onExportTable,
    onSearch,
  }: TableMenuProps) => {
    return (
      <div className={className}>
        <Button
          variant="contained"
          color="primary"
          size="xxs"
          onClick={() => onAddItem?.()}
        >
          + New User Role
        </Button>
        <div
          style={{
            display: "flex",
            flex: "initial",
            flexDirection: "row",
            gap: 5,
            alignContent: "center",
            alignItems: "center",
            marginLeft: "auto",
          }}
        >
          <DropDownMenu
            data={columnState.map((col: ColumnProps<UserRole>) => ({
              label: col.caption,
              value: col.dataField,
            }))}
            defaultValue={columnState
              .filter((col: ColumnProps<UserRole>) => col.visible !== false)
              .map((col: ColumnProps<UserRole>) => ({
                label: col.caption,
                value: col.dataField,
              }))}
            variant="outlined"
            multiselect
            searchable
            onChange={handleColumnVisibility}
            dropDownProps={{
              style: { width: 175, maxWidth: 400 },
            }}
            buttonProps={{
              title: "Show/Hide Columns",
              size: "xxs",
              style: { padding: "0px 4px" },
            }}
          >
            <Columns3Icon size={14} />
          </DropDownMenu>
          <DropDownMenu
            variant="outlined"
            data={[
              { label: "Export Visible Columns", value: "export:visible" },
              { label: "Export All Columns", value: "export:all" },
            ]}
            onItemSelect={(item) => onExportTable?.(item.value.toString())}
            dropDownProps={{
              style: { width: 175, maxWidth: 400 },
            }}
            buttonProps={{
              title: "Export List to XLSX",
              size: "xxs",
              style: { padding: "0px 4px" },
            }}
          >
            <DownloadIcon size={14} />
          </DropDownMenu>
          <TextInput
            placeholder="Search User Roles"
            onKeyUp={onSearch}
            label={undefined}
            error={undefined}
            required={undefined}
          />
        </div>
      </div>
    );
  }
)`
  display: flex;
  flex-direction: row;
  gap: 10px;
  margin-bottom: 10px;
`;

const UserRolesTable = styled(
  ({
    className,
    onAddItem,
    onRowAction,
    tableRef,
    selectedRow,
  }: UserRolesTableProps) => {
    const [query, setQuery] = useState<Query>({
      pageSize,
    });

    const [columnState, setColumnState] = useState(() => {
      return synchronizeColumnState(
        UserRoleDefs,
        JSON.parse(localStorage.getItem(stateStoreKey) || "{}")
      );
    });

    const { data, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
      useInfiniteQuery({
        queryKey: [...queryKeyPrefix, query],
        queryFn: ({ pageParam, queryKey }) => {
          const [, , query] = queryKey as [string, string, Query];
          return UserRolesAPI.get({
            ...query,
            page: pageParam,
          });
        },
        getNextPageParam: (lastPage) => {
          return lastPage.nextPage;
        },
        getPreviousPageParam: (firstPage) => {
          if (firstPage.page - 1 === 0) return null;
          return firstPage.page - 1;
        },
        initialPageParam: 1,
        placeholderData: (data) => data,
      });

    const records = data?.pages?.reduce((acc, page) => {
      return [...acc, ...page.data];
    }, []);

    const debouncedFetchNextPage = useDebouncedCallback(() => {
      fetchNextPage();
    }, 50);

    // Detect scroll to bottom
    const handleScroll = (
      e:
        | React.UIEvent<HTMLDivElement, UIEvent>
        | React.FormEvent<HTMLDivElement>
    ) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (
        scrollHeight - scrollTop <=
        clientHeight + 100 * (data?.pages?.length || 1)
      ) {
        if (hasNextPage && !isFetchingNextPage) {
          debouncedFetchNextPage();
        }
      }
    };

    const handleExportTable = (option: string) => {
      let columns = columnState.map((c) => {
        return { dataField: c.dataField, header: c.caption, ...c };
      });
      if (option === "export:visible") {
        columns = columns.filter((c) => c.visible !== false);
      }

      UserRolesAPI.exportList({
        query,
        type: "xlsx",
        columns,
        date_format: getDateFormat({
          isMoment: true,
          includeTime: true,
        }),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }).then((res) => {
        const { signedUrl, filename } = res;
        const el = document.createElement("a");
        el.href = signedUrl.replace(
          "http://localhost:3000",
          "http://localhost:3001"
        );
        el.download = filename;
        el.click();
        el.remove();
      });
    };

    const handleSearch = (e: any) => {
      let searchText = e.target.value;
      if (e.code === "Enter" || e.code === "NumpadEnter" || searchText === "") {
        setQuery((q) => ({
          ...q,
          search: searchText === "" ? null : searchText,
        }));
      }
    };

    const handleColumnVisibility = (columns: any) => {
      setColumnState((cs) => {
        return cs.map((c) => {
          if (columns.find((col: any) => col.value === c.dataField)) {
            return {
              ...c,
              visible: true,
            };
          }
          return {
            ...c,
            visible: false,
          };
        });
      });
    };

    const handleActionButtonClick = (rowData: any) => {
      onRowAction?.(rowData);
    };

    useEffect(() => {
      if (tableRef) {
        tableRef.current = {
          refresh: refetch,
        };
      }
    }, [tableRef, refetch]);

    return (
      <div className={className}>
        <TableMenu
          columnState={columnState}
          handleColumnVisibility={handleColumnVisibility}
          onExportTable={handleExportTable}
          onSearch={handleSearch}
          onAddItem={onAddItem}
        />
        <Table
          data={records || []}
          keyValue="uuid"
          columnProps={{ minWidth: 150, width: 150 }}
          onScroll={handleScroll}
          onActionButtonClick={handleActionButtonClick}
          showActionColumn={true}
          focusedRow={selectedRow}
        >
          {columnState.map((col) => (
            <Column key={col.dataField} {...col} />
          ))}
        </Table>
      </div>
    );
  }
)`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`;

const UserRoles = styled(({ className }: UserRolesProps) => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { hasPermission, MONOLITH_PERMISSIONS }: any = usePermissions();
  const tableRef = useRef<{ refresh: () => {} }>(null);

  const [selectedItem, setSelectedItem] = useState<any>({
    uuid,
  });
  const [showFlyout, setShowFlyout] = useState(uuid ? true : false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleShowFlyout = (rowData: any) => {
    setSelectedItem(rowData);
    setShowFlyout(true);
    navigate(`/team/roles/${rowData.uuid}`);
  };

  const handleCloseFlyout = () => {
    // remove event_uuid from URL
    const newUrl = `/team/roles`;
    navigate(newUrl, { replace: true });
    setSelectedItem(null);
    setShowFlyout(false);
  };

  const onCreate = () => {
    tableRef.current?.refresh();
  };

  // keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // open modal on ctrl + n
      if (e.ctrlKey && e.key === "n") {
        e.preventDefault();
        setShowCreateModal(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  if (!hasPermission(MONOLITH_PERMISSIONS.USER_ROLES_MANAGE)) {
    return <AccessDeniedMessage />;
  }

  return (
    <div className={className}>
      <CreateUserRoleModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCancel={() => setShowCreateModal(false)}
        onSubmit={onCreate}
      />
      <UserRoleFlyout
        show={showFlyout}
        onClose={handleCloseFlyout}
        uuid={selectedItem?.uuid}
        defaultInfo={{ ...selectedItem }}
        onEdit={() => {
          tableRef?.current?.refresh();
        }}
        onDelete={() => {
          tableRef?.current?.refresh();
          handleCloseFlyout();
        }}
      />
      <UserRolesTable
        tableRef={tableRef}
        onAddItem={() => setShowCreateModal(true)}
        onRowAction={handleShowFlyout}
        selectedRow={selectedItem}
      />
    </div>
  );
})`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;

  .button-menu {
    display: flex;
    flex-direction: row;
    gap: 10px;
    margin-bottom: 10px;
  }
`;

export default UserRoles;
